import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { addLeadChannel, deleteLeadChannel, getLeadSocials, refreshLeadChannel, refreshLeadSocials, type LeadChannel, type LeadSocialProfile, type LeadSocials } from '../api/crm';
import { useAuth } from '../auth/AuthContext';
import { ChannelPanel, PLATFORM_LABEL, channelName, fmtSocialValue } from '../affilmaster/AffiliatePropagaceSection';
import { DialogShell, confirmDialog } from '../shell/dialogs';
import { useFeather } from '../shell/useFeather';

function channelKey(url: string): string {
  return url.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
}

function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function shortUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
}

export function CrmLeadChannels({
  leadId,
  channels,
  canEdit,
  onChanged,
}: {
  leadId: number;
  channels: LeadChannel[];
  canEdit: boolean;
  onChanged: (next: LeadChannel[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<number | 'add' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [broken, setBroken] = useState<Record<number, boolean>>({});
  const pending = channels.some((c) => c.fetchStatus === 'pending');
  const { me } = useAuth();
  const socialsEnabled = me?.activeSpaceSlug === 'affilmaster';
  const [socials, setSocials] = useState<LeadSocials | null>(null);
  const [socialsBusy, setSocialsBusy] = useState(false);
  const [statsOf, setStatsOf] = useState<LeadSocialProfile | null>(null);
  const channelSig = channels.map((c) => c.url).join('|');
  useFeather([channels, adding, busyId, socials, socialsBusy]);

  useEffect(() => {
    if (!socialsEnabled || channels.length === 0) { setSocials(null); return; }
    let cancelled = false;
    setSocialsBusy(true);
    getLeadSocials(leadId)
      .then(async (first) => {
        if (cancelled) return;
        setSocials(first);
        if (!first.stale) return;
        const fresh = await refreshLeadSocials(leadId, false);
        if (!cancelled) setSocials(fresh);
      })
      .catch(() => { if (!cancelled) setSocials(null); })
      .finally(() => { if (!cancelled) setSocialsBusy(false); });
    return () => { cancelled = true; };
  }, [leadId, socialsEnabled, channelSig, channels.length]);

  const profileFor = (url: string): LeadSocialProfile | null => socials?.profiles.find((p) => channelKey(p.url) === channelKey(url)) ?? null;

  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => onChanged(channels), 4000);
    return () => clearTimeout(t);
  }, [pending, channels, onChanged]);

  function run(id: number | 'add', action: () => Promise<LeadChannel[]>) {
    setBusyId(id);
    setError(null);
    action()
      .then((next) => { onChanged(next); if (id === 'add') { setDraft(''); setAdding(false); } })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Akce se nepodařila'))
      .finally(() => setBusyId(null));
  }

  async function remove(ch: LeadChannel) {
    if (!(await confirmDialog({ title: 'Odebrat kanál?', message: `${shortUrl(ch.url)} zmizí z karty leadu.`, confirmLabel: 'Odebrat', danger: true }))) return;
    run(ch.id, () => deleteLeadChannel(leadId, ch.id));
  }

  return (
    <div className="udaje-section crm-channels">
      <div className="udaje-section-head">
        <span>Propagační kanály{channels.length ? <span className="crm-channels-count">{channels.length}</span> : null}</span>
        {canEdit && channels.length < 5 && !adding && (
          <button type="button" className="crm-channels-plus" title="Přidat kanál" aria-label="Přidat kanál" onClick={() => setAdding(true)}><i data-feather="plus" aria-hidden="true" /></button>
        )}
      </div>
      <div className="udaje-card">
        {channels.length === 0 && <div className="crm-channels-empty">Lead zatím nemá žádný kanál.</div>}
        {channels.map((ch) => {
          const prof = profileFor(ch.url);
          const title = prof?.title || ch.title || ch.siteName || hostLabel(ch.url);
          const letter = hostLabel(ch.url).charAt(0).toUpperCase();
          const iconUrl = prof?.avatarUrl || ch.faviconUrl;
          return (
            <div key={ch.id} className={`crm-channel is-${ch.fetchStatus}`}>
              <span className={`crm-channel-icon${prof?.avatarUrl ? ' is-avatar' : ''}`} aria-hidden="true">
                {iconUrl && !broken[ch.id]
                  ? <img src={iconUrl} alt="" loading="lazy" onError={() => setBroken((b) => ({ ...b, [ch.id]: true }))} />
                  : <i>{letter}</i>}
              </span>
              <span className="crm-channel-body">
                <a className="crm-channel-title" href={ch.url} target="_blank" rel="noreferrer">{title}</a>
                <a className="crm-channel-url" href={ch.url} target="_blank" rel="noreferrer">{shortUrl(ch.url)}</a>
                {ch.description && <span className="crm-channel-desc">{ch.description}</span>}
                {ch.fetchStatus === 'pending' && <span className="crm-channel-note">Načítám náhled stránky…</span>}
                {ch.fetchStatus === 'failed' && <span className="crm-channel-note is-failed">Náhled stránky se nepodařilo načíst{ch.httpStatus ? ` (HTTP ${ch.httpStatus})` : ''}.</span>}
                {socialsEnabled && prof?.primary.value == null && prof?.lastError && <span className="crm-channel-note is-failed">{prof.lastError}</span>}
                {socialsEnabled && (!prof || (prof.primary.value == null && !prof.lastError)) && socialsBusy && <span className="crm-channel-note">Načítám statistiky…</span>}
              </span>
              {socialsEnabled && prof && prof.primary.value != null && (
                <button type="button" className="crm-channel-stat" onClick={() => setStatsOf(prof)} title="Zobrazit statistiky kanálu">
                  <span><small>{PLATFORM_LABEL[prof.platform]}</small>{prof.primary.label}</span>
                  <strong>{fmtSocialValue(prof.primary.value)}</strong>
                </button>
              )}
              {canEdit && (
                <span className="crm-channel-actions">
                  <button type="button" title="Načíst náhled znovu" aria-label="Načíst náhled znovu" disabled={busyId != null} onClick={() => run(ch.id, () => refreshLeadChannel(leadId, ch.id))}><i data-feather="refresh-cw" aria-hidden="true" /></button>
                  <button type="button" title="Odebrat kanál" aria-label="Odebrat kanál" disabled={busyId != null} onClick={() => void remove(ch)}><i data-feather="trash-2" aria-hidden="true" /></button>
                </span>
              )}
            </div>
          );
        })}
        {canEdit && adding && (
          <form
            className="crm-channel-add"
            onSubmit={(e) => { e.preventDefault(); if (draft.trim()) run('add', () => addLeadChannel(leadId, draft.trim())); }}
          >
            <input
              className="udaje-input"
              value={draft}
              autoFocus
              placeholder="https://… (web, Instagram, YouTube, Discord)"
              disabled={busyId != null}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setAdding(false); setDraft(''); } }}
            />
            <button type="submit" className="btn btn-primary btn-sm" disabled={busyId != null || !draft.trim()}>{busyId === 'add' ? 'Přidávám…' : 'Přidat'}</button>
            <button type="button" className="btn btn-link btn-sm crm-channel-cancel" disabled={busyId != null} onClick={() => { setAdding(false); setDraft(''); setError(null); }}>Zrušit</button>
          </form>
        )}
        {error && <div className="crm-channels-error">{error}</div>}
      </div>
      {statsOf && (
        <DialogShell eyebrow={PLATFORM_LABEL[statsOf.platform]} title={channelName(statsOf)} onClose={() => setStatsOf(null)} className="am-prop-dialog">
          <div className="am-prop-modal-body">
            <ChannelPanel p={statsOf} refreshing={false} compact />
          </div>
        </DialogShell>
      )}
    </div>
  );
}
